/**
 * The printed breakdown's entire stylesheet. Nothing from the app cascade is
 * loaded into the print frame — not Bootstrap's print sheet (which forces
 * `body { min-width: 992px }` and `@page { size: a3 }`), not antd's CSS-in-JS,
 * not the global `html { zoom: 0.8 }` — so every rule the document needs lives
 * here, and what you see in preview is what every user gets.
 *
 * Ink-first: one accent for the title and section labels, thin rules, two very
 * light fills. `print-color-adjust: exact` makes those fills print whether or
 * not the user ticked "Background graphics", so the output is deterministic.
 */
export const PRINT_CSS = `
  /* margin:0 is deliberate. Chrome draws its own print header/footer — the
     document title on the left, the page URL ("about:srcdoc") on the right —
     INSIDE the @page margin box, and there is no CSS switch for them. With no
     margin box there is nowhere to draw them. The page's real margins are put
     back below as padding on the wrapper table's cells: horizontal padding on a
     cell holds across every page fragment, and the thead/tfoot repeat per page,
     so their vertical padding becomes the per-page top and bottom margin. */
  @page { size: letter landscape; margin: 0; }

  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body {
    font-family: Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #1f1f29;
    font-size: 10.5px;
    line-height: 1.4;
  }

  /* One-column wrapper table: Chromium repeats its thead (and tfoot) on every
     printed page, which is how the running header/footer are achieved without
     @page margin boxes. */
  table.doc { width: 100%; border-collapse: collapse; }
  table.doc > thead > tr > td { padding: 0.45in 0.5in 0; }
  table.doc > tbody > tr > td { padding: 0 0.5in; }
  table.doc > tfoot > tr > td { padding: 0 0.5in 0.4in; }

  .hdr { display: flex; align-items: center; justify-content: space-between; padding-bottom: 8px; margin-bottom: 14px; border-bottom: 1.5px solid #6b43b5; }
  .hdr-left { display: flex; align-items: center; gap: 14px; }
  .hdr img { height: 22px; width: auto; display: block; }
  .hdr-title { font-size: 13px; font-weight: 600; color: #6b43b5; letter-spacing: 0.2px; }
  .hdr-right { text-align: right; font-size: 10px; color: #6f6c80; line-height: 1.35; }
  .hdr-right strong { color: #1f1f29; font-weight: 600; font-size: 11px; }

  .ftr { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; padding-top: 8px; margin-top: 14px; border-top: 1px solid #e3e0ec; font-size: 8.5px; color: #6f6c80; }
  .ftr .disclaimer { max-width: 70%; }

  .section { margin-bottom: 14px; break-inside: avoid; page-break-inside: avoid; }
  .section-title { font-size: 9px; font-weight: 600; color: #6b43b5; text-transform: uppercase; letter-spacing: 0.6px; margin: 0 0 6px; }
  .section-title.stacked { margin-top: 14px; }

  /* Summary: Patient over Office on the left, Member & Plan taking the rest in
     two sub-columns — half the height of a single 11-row list, and wide enough
     that addresses and date ranges stop wrapping. */
  .summary { display: grid; grid-template-columns: 1fr 2fr; gap: 0 32px; align-items: start; }
  .kv-split { display: grid; grid-template-columns: 1fr 1fr; gap: 0 28px; align-items: start; }
  .kv { display: grid; grid-template-columns: 100px 1fr; gap: 2px 10px; align-content: start; }
  .kv .k { color: #6f6c80; }
  .kv .v { color: #1f1f29; word-break: break-word; }

  /* Category summary + procedures share one table style. */
  table.grid { width: 100%; border-collapse: collapse; table-layout: fixed; }
  table.grid th, table.grid td { padding: 5px 7px; text-align: left; vertical-align: top; border-bottom: 1px solid #e3e0ec; word-break: break-word; }
  table.grid thead { display: table-header-group; }
  table.grid thead th { font-size: 9px; font-weight: 600; color: #6f6c80; text-transform: uppercase; letter-spacing: 0.3px; white-space: nowrap; background: #f6f5fa; border-bottom: 1px solid #d9d5e6; }
  table.grid tbody tr { break-inside: avoid; page-break-inside: avoid; }
  table.grid td.num { white-space: nowrap; }
  table.grid tr.group td { background: #f6f5fa; font-weight: 600; color: #4d3f7a; font-size: 10px; padding-top: 6px; padding-bottom: 4px; border-bottom: 1px solid #d9d5e6; }
  table.grid td.code { font-variant-numeric: tabular-nums; font-weight: 600; }
  /* Poppins has no geometric-shape glyphs, so name fonts that do (Windows, macOS,
     Linux) ahead of the generic fallback, or the cooldown "◐" prints as a speck. */
  table.grid td.glyph { text-align: center; font-size: 12px; line-height: 1; padding-top: 6px; font-family: "Segoe UI Symbol", "Apple Symbols", "Noto Sans Symbols2", "DejaVu Sans", sans-serif; }

  .elig-label { font-weight: 600; }
  .elig-line { color: #4a4757; }
  .elig-verbatim { color: #6f6c80; font-style: italic; }
  .muted { color: #6f6c80; }
  .note-list { margin: 0; padding: 0; list-style: none; }
  .note-list li { margin: 0 0 1px; line-height: 1.3; }

  .unavailable { font-size: 10px; }
  .unavailable .codes { margin-top: 3px; column-count: 3; column-gap: 24px; }
  .unavailable .codes div { break-inside: avoid; }

  .empty { color: #6f6c80; font-style: italic; }

  /* The Plan section (the Breakdowns tab's Plan card on paper). */
  .plan-grid { display: grid; grid-template-columns: 1fr 1.6fr 1fr; gap: 0 28px; align-items: start; margin-bottom: 8px; }
  .sub-title { font-size: 8.5px; font-weight: 600; color: #6f6c80; text-transform: uppercase; letter-spacing: 0.4px; margin: 0 0 3px; }
  .pool { margin-bottom: 4px; }
  .facts { display: grid; grid-template-columns: repeat(4, 1fr); gap: 3px 18px; }
  .facts .k { color: #6f6c80; font-size: 9px; }
  .facts .v { color: #1f1f29; word-break: break-word; }
  .small { font-size: 9px; }
  .code { font-variant-numeric: tabular-nums; color: #6f6c80; margin-left: 4px; }
  .tone-bad { color: #b3261e; }
  .tone-warn { color: #9a5b00; }
  .tone-ok { color: #2f7d32; }
  .shared { color: #5b3aa0; font-size: 9.5px; }
  .rule { color: #9a5b00; font-size: 9.5px; }

  /* Image export (Copy Image): the same page, laid out once at paper width for
     a screen capture instead of paginated printing. html2canvas cannot render
     CSS multi-column, so the unavailable-codes list wraps as a flex row here.
     It also measures text with the DOM's font but paints it with its own canvas
     face, and with the Poppins webfont the two disagree — words run together or
     drift apart. System faces measure and paint identically, so the image uses
     them; the printed/PDF page keeps Poppins. */
  html[data-export="image"], html[data-export="image"] body { width: 10in; }
  html[data-export="image"] body {
    min-height: 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif;
  }
  html[data-export="image"] .unavailable .codes { column-count: auto; display: flex; flex-wrap: wrap; gap: 2px 24px; }
`;
